import { billingDetailEffects, billingListEffects } from './billing.actions';
import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';
import { listPageAction } from '@/shared/patterns/list-search';
import { filterAction } from '@/shared/filters/filter-action';
import { BILLING_STATUS } from './billing.enums';
import { billingFilters } from './billing.filters';
import { BILLING_PATH } from './billing.search';
export const billingOpen = action({
  id: 'billing.open',
  kind: 'navigate',
  label: msg`Abrir cobrança`,
  description: msg`Abre uma cobrança da lista: dados, histórico, categorias, Pix e boleto. Nada muda só por abrir.`,
  input: z.object({ id: z.string() }),
  run: ({ nav, input }) => nav.to(`${BILLING_PATH}/${input.id}`),
});
/**
 * Convênio da cobrança (`ChargesFormContainer.tsx:181`): a lista de Carteiras filtrada por `walletsCode`, na mesma
 * aba. Intenção do bloco `billing.data` resolvida pelo host (U41); caminho literal para não fechar ciclo com `wallet`.
 */
export const billingOpenWallet = action({
  id: 'billing.openWallet',
  kind: 'navigate',
  label: msg`Abrir convênio`,
  description: msg`Abre, na mesma aba, a lista de carteiras filtrada pelo convênio desta cobrança. Nada muda só por abrir.`,
  input: z.object({ code: z.string().min(1) }),
  run: ({ nav, input }) => nav.to('/gestao-carteira/carteiras', { search: { walletsCode: [input.code] } }),
});
export const billingRegisterFromList = action({
  id: 'billing.registerFromList',
  kind: 'draft',
  label: msg`Registrar boleto`,
  description: msg`Abre o registro do boleto de uma cobrança da lista ainda não registrada, sem sair da lista. Nada é registrado sem a confirmação da pessoa.`,
  input: z.object({ id: z.string() }),
  run: ({ nav, input }) => nav.search({ sheet: 'new', draft: input.id }),
});
export const billingEdit = action({
  id: 'billing.edit',
  kind: 'draft',
  label: msg`Edição rápida`,
  description: msg`Abre a edição rápida de uma cobrança da lista ainda não registrada. Nada é salvo sem a confirmação da pessoa.`,
  input: z.object({ id: z.string() }),
  run: ({ nav, input }) => nav.search({ edit: input.id }),
});
export const billingSelect = action({
  id: 'billing.select',
  kind: 'draft',
  label: msg`Selecionar cobranças`,
  description: msg`Marca cobranças da lista, mesmo em páginas diferentes, e mostra a soma dos valores. As ações em lote só valem para as selecionadas da página atual.`,
  input: z.object({ ids: z.array(z.string()) }),
  run: ({ nav, input }) => nav.search({ selected: input.ids }),
});
export const billingPage = listPageAction('billing.page');
export const billingFilter = filterAction('billing.filter', billingFilters);
export const billingListScreen = defineScreen({
  id: 'billing.list',
  path: BILLING_PATH,
  title: msg`Cobranças`,
  description: msg`Lista das cobranças da carteira, com filtros, seleção com total, Nova cobrança, importação e exportação CNAB, exportação CSV e as ações em lote das selecionadas (baixa, transferência de carteira, instrução e envio).`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'receipt-text', section: 'wallet-management' },
  actions: [
    billingOpen,
    billingRegisterFromList,
    billingEdit,
    billingPage,
    billingFilter,
    billingSelect,
    ...billingListEffects,
  ],
});
export const billingDetailScreen = defineScreen({
  id: 'billing.detail',
  path: `${BILLING_PATH}/$billingId`,
  title: msg`Cobrança`,
  description: msg`Ficha de uma cobrança: dados do pagador e dos valores, situação do pagamento, histórico, categorias, Pix e boleto, com as ações que o status permite (registrar, alterar, baixa, e-mail, régua de comunicação, comentário e emolumento).`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'receipt-text', parent: 'billing.list', record: msg`Cobrança` },
  actions: [...billingDetailEffects],
});

/** O que o agente lê da lista (U24, U46 D2-C): página, total, filtros e seleção, sem pagador nem documento. */
export function billingListContext(input: {
  readonly page: number;
  readonly total: number | undefined;
  readonly filters: number;
  readonly selected: number;
}): ScreenContext {
  const { page, total, filters, selected } = input;
  return {
    summary: [
      t`Cobranças, página ${page}.`,
      total === undefined ? t`Carregando.` : t`Cobranças encontradas: ${total}.`,
      filters > 0 ? t`Filtros aplicados: ${filters}.` : t`Sem filtros.`,
      selected > 0 ? t`Selecionadas nesta página: ${selected}.` : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { page, total: total ?? null, filters, selectedCount: selected },
    sensitivity: 'internal',
  };
}

/** O que o agente lê da cobrança aberta (U24, U46 D2-C): o status, sem pagador, documento nem número. */
export function billingDetailContext(row: { readonly id: string; readonly statusValue: string; readonly statusDisplay?: string | null | undefined } | undefined): ScreenContext {
  if (!row) return { summary: t`Cobrança carregando.`, fields: { status: null }, sensitivity: 'internal' };
  const status = BILLING_STATUS.label(i18n, row.statusValue, row.statusDisplay ?? row.statusValue) ?? row.statusValue;
  return {
    summary: t`Cobrança aberta, com status ${status}.`,
    entity: { type: 'Billing', id: row.id },
    fields: { status: row.statusValue },
    sensitivity: 'internal',
  };
}
